import React, { useState, useEffect } from "react";
import { dataLoaderUri } from "../API/API";
import apiClient from "../services/apiClient";
import BackButton from "../shared/BackButton/BackButton";
import styles from "./CourseProgress.module.css";

interface ComponentStatus {
  total: number;
  status: "complete" | "partial" | "missing" | "error";
  percentage: number;
  details?: string;
}

interface SectionProgress {
  section_id: string;
  section_name: string;
  disabled_components: string[];
  vocabulary: ComponentStatus;
  passages: ComponentStatus;
  practice_sentences: ComponentStatus;
  conversation_objectives: ComponentStatus;
  item_guess: ComponentStatus;
  reconstruct_exercises: ComponentStatus;
  item_describe: ComponentStatus;
  image_list: ComponentStatus;
  sentence_comprehension: ComponentStatus;
  overall_completion: number;
}

interface CourseProgressData {
  target_language: string;
  base_language: string;
  target_language_name: string;
  base_language_name: string;
  sections: SectionProgress[];
  summary: {
    total_sections: number;
    complete_sections: number;
    partial_sections: number;
    missing_sections: number;
    overall_completion: number;
  };
}

const LANGUAGES = [
  { code: "EN", name: "English" },
  { code: "ES", name: "Spanish" },
  { code: "FR", name: "French" },
  { code: "DE", name: "German" },
  { code: "IT", name: "Italian" },
  { code: "PT", name: "Portuguese" },
];

const FILTER_OPTIONS = [
  { value: "all", label: "All Sections" },
  { value: "complete", label: "Complete (≥90%)" },
  { value: "partial", label: "Partial (10-89%)" },
  { value: "missing", label: "Missing (<10%)" },
];

const CourseProgress: React.FC = () => {
  const [targetLanguage, setTargetLanguage] = useState<string>("ES");
  const [baseLanguage, setBaseLanguage] = useState<string>("EN");
  const [domain, setDomain] = useState<string>("General");
  const [availableDomains, setAvailableDomains] = useState<string[]>(["General"]);
  const [progressData, setProgressData] = useState<CourseProgressData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [filter, setFilter] = useState<string>("all");
  const [sortColumn, setSortColumn] = useState<string>("section_id");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [startSection, setStartSection] = useState<number>(0);
  const [endSection, setEndSection] = useState<number>(10);
  const [totalSections, setTotalSections] = useState<number>(0);

  // Remove automatic data fetching - user must press "Refresh Data" button to load data

  const fetchProgressData = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await apiClient.get(
        `${dataLoaderUri}/api/v1/course-progress/${targetLanguage}/${baseLanguage}?start=${startSection}&end=${endSection}&domain=${domain}`
      );

      setProgressData(data);
      if (data.total_available) {
        setTotalSections(data.total_available);
      }
      if (data.available_domains && data.available_domains.length > 0) {
        setAvailableDomains(data.available_domains);
      }
    } catch (err: any) {
      if (err.response) {
        setError(
          `Failed to fetch progress data: ${err.response.status} - ${err.response.data?.message || err.response.statusText}`
        );
      } else {
        setError(`Network error: ${err instanceof Error ? err.message : "Unknown error"}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (
    status: ComponentStatus,
    isDisabled: boolean = false,
    componentType?: string
  ): string => {
    if (isDisabled) {
      return "⚪"; // Grey circle for disabled components
    }

    // Special logic for vocabulary: any vocab present gets green checkmark
    if (componentType === "vocabulary" && status.total > 0) {
      return "✅";
    }

    switch (status.status) {
      case "complete":
        return "✅";
      case "partial":
        return "⚠️";
      case "missing":
        return "❌";
      case "error":
        return "🔧";
      default:
        return "❓";
    }
  };

  const isComponentDisabled = (section: SectionProgress, componentName: string): boolean => {
    // Safety check: return false if disabled_components is undefined or null
    if (!section.disabled_components || !Array.isArray(section.disabled_components)) {
      return false;
    }

    // Map frontend component names to backend disabled component names
    const componentMapping: { [key: string]: string[] } = {
      vocabulary: ["Flashcards", "Matching", "Memory"],
      passages: ["Passage"],
      practice_sentences: ["Fill in the blank", "Multiple Choice"],
      conversation_objectives: ["Conversation"],
      item_guess: ["Item Guess"],
      reconstruct_exercises: ["Pronunciation"],
      item_describe: ["Item Describe", "Item Description"],
      image_list: ["Photolist"],
      sentence_comprehension: [
        "Reading Comprehension Base",
        "Reading Comprehension Target",
        "Listening Comprehension Base",
        "Listening Comprehension Target",
      ],
    };

    const backendNames = componentMapping[componentName] || [componentName];
    return backendNames.some((name) => section.disabled_components.includes(name));
  };

  const getCellClassName = (
    section: SectionProgress,
    componentName: string,
    statusClass: string
  ): string => {
    if (isComponentDisabled(section, componentName)) {
      return `${statusClass} ${styles.disabled}`;
    }
    return statusClass;
  };

  const getStatusColor = (status: ComponentStatus): string => {
    switch (status.status) {
      case "complete":
        return styles.complete;
      case "partial":
        return styles.partial;
      case "missing":
        return styles.missing;
      case "error":
        return styles.error;
      default:
        return "";
    }
  };

  const filterSections = (sections: SectionProgress[]): SectionProgress[] => {
    return sections.filter((section) => {
      switch (filter) {
        case "complete":
          return section.overall_completion >= 90;
        case "partial":
          return section.overall_completion >= 10 && section.overall_completion < 90;
        case "missing":
          return section.overall_completion < 10;
        default:
          return true;
      }
    });
  };

  const sortSections = (sections: SectionProgress[]): SectionProgress[] => {
    return [...sections].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "section_id":
          // Convert section_id to numbers for proper numerical sorting
          aValue = parseInt(a.section_id) || 0;
          bValue = parseInt(b.section_id) || 0;
          break;
        case "section_name":
          aValue = a.section_name;
          bValue = b.section_name;
          break;
        case "overall_completion":
          aValue = a.overall_completion;
          bValue = b.overall_completion;
          break;
        case "vocabulary":
          aValue = a.vocabulary.total;
          bValue = b.vocabulary.total;
          break;
        default:
          // Default sorting by section_id as numbers
          aValue = parseInt(a.section_id) || 0;
          bValue = parseInt(b.section_id) || 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column: string): string => {
    if (sortColumn !== column) return "↕️";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const exportToCSV = () => {
    if (!progressData) return;

    const headers = [
      "Section ID",
      "Section Name",
      "Overall %",
      "Vocabulary",
      "Passages",
      "Fill In The Blank",
      "Conversations",
      "Item Guess",
      "Reconstruct",
      "Item Describe",
      "Image List",
      "Sentence Comp",
    ];

    const rows = progressData.sections.map((section) => [
      section.section_id,
      section.section_name,
      section.overall_completion,
      `${section.vocabulary.total} (${section.vocabulary.percentage}%)`,
      `${section.passages.total} (${section.passages.percentage}%)`,
      `${section.practice_sentences.total} (${section.practice_sentences.percentage}%)`,
      `${section.conversation_objectives.total} (${section.conversation_objectives.percentage}%)`,
      `${section.item_guess.total} (${section.item_guess.percentage}%)`,
      `${section.reconstruct_exercises.total} (${section.reconstruct_exercises.percentage}%)`,
      `${section.item_describe.total} (${section.item_describe.percentage}%)`,
      `${section.image_list.total} (${section.image_list.percentage}%)`,
      `${section.sentence_comprehension.total} (${section.sentence_comprehension.percentage}%)`,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `course-progress-${targetLanguage}-${baseLanguage}-sections-${startSection}-${endSection - 1}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Calculate adjusted overall completion excluding disabled components
  const calculateAdjustedOverallCompletion = (section: SectionProgress): number => {
    const componentMapping: { [key: string]: string[] } = {
      vocabulary: ["Flashcards", "Matching", "Memory"],
      passages: ["Passage"],
      practice_sentences: ["Fill in the blank", "Multiple Choice"],
      conversation_objectives: ["Conversation"],
      item_guess: ["Item Guess"],
      reconstruct_exercises: ["Pronunciation"],
      item_describe: ["Item Describe", "Item Description"],
      image_list: ["Photolist"],
      sentence_comprehension: [
        "Reading Comprehension Base",
        "Reading Comprehension Target",
        "Listening Comprehension Base",
        "Listening Comprehension Target",
      ],
    };

    const components = [
      { name: "vocabulary", status: section.vocabulary },
      { name: "passages", status: section.passages },
      { name: "practice_sentences", status: section.practice_sentences },
      { name: "conversation_objectives", status: section.conversation_objectives },
      { name: "item_guess", status: section.item_guess },
      { name: "reconstruct_exercises", status: section.reconstruct_exercises },
      { name: "item_describe", status: section.item_describe },
      { name: "image_list", status: section.image_list },
      { name: "sentence_comprehension", status: section.sentence_comprehension },
    ];

    // Filter out disabled components
    const enabledComponents = components.filter((comp) => {
      const backendNames = componentMapping[comp.name] || [comp.name];
      const isDisabled = backendNames.some((name) => section.disabled_components?.includes(name));
      return !isDisabled;
    });

    if (enabledComponents.length === 0) {
      return 0;
    }

    // For vocabulary, if total > 0, treat as 100% for calculation
    const adjustedPercentages = enabledComponents.map((comp) => {
      if (comp.name === "vocabulary" && comp.status.total > 0) {
        return 100;
      }
      return comp.status.percentage;
    });

    const totalPercentage = adjustedPercentages.reduce((sum, pct) => sum + pct, 0);
    return Math.round((totalPercentage / enabledComponents.length) * 10) / 10;
  };

  const defaultStatus: ComponentStatus = { total: 0, status: "missing", percentage: 0 };

  // Process sections to update their overall completion
  const processedSections = progressData
    ? progressData.sections.map((section) => {
        const normalized = {
          ...section,
          sentence_comprehension: section.sentence_comprehension ?? defaultStatus,
        };
        return {
          ...normalized,
          overall_completion: calculateAdjustedOverallCompletion(normalized),
        };
      })
    : [];

  const displayedSections = processedSections
    ? sortSections(filterSections(processedSections))
    : [];

  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>Course Progress Dashboard</h1>

      {/* Language Selection */}
      <div className={styles.controls}>
        <div className={styles.languageSection}>
          <div className={styles.languageSelector}>
            <label className={styles.label}>Target Language:</label>
            <select
              value={targetLanguage}
              onChange={(e) => setTargetLanguage(e.target.value)}
              className={styles.select}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.languageSelector}>
            <label className={styles.label}>Base Language:</label>
            <select
              value={baseLanguage}
              onChange={(e) => setBaseLanguage(e.target.value)}
              className={styles.select}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.languageSelector}>
            <label className={styles.label}>Course:</label>
            <select
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className={styles.select}
            >
              {availableDomains.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.filterSection}>
          <label className={styles.label}>Filter:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={styles.select}
          >
            {FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.rangeSection}>
          <div className={styles.rangeInput}>
            <label className={styles.label}>Start Section:</label>
            <input
              type="number"
              value={startSection}
              onChange={(e) => setStartSection(Math.max(0, parseInt(e.target.value) || 0))}
              className={styles.numberInput}
              min="0"
            />
          </div>
          <div className={styles.rangeInput}>
            <label className={styles.label}>End Section:</label>
            <input
              type="number"
              value={endSection}
              onChange={(e) => setEndSection(Math.max(1, parseInt(e.target.value) || 1))}
              className={styles.numberInput}
              min="1"
            />
          </div>
          {totalSections > 0 && (
            <div className={styles.totalInfo}>
              <span className={styles.label}>Total Available: {totalSections}</span>
            </div>
          )}
        </div>

        <div className={styles.navigationSection}>
          <button
            onClick={() => {
              const prevStart = Math.max(0, startSection - 20);
              const prevEnd = Math.max(20, endSection - 20);
              setStartSection(prevStart);
              setEndSection(prevEnd);
            }}
            disabled={startSection === 0}
            className={styles.navButton}
          >
            ← Previous 20
          </button>

          <button
            onClick={() => {
              const nextStart = startSection + 20;
              const nextEnd = Math.min(totalSections, endSection + 20);
              if (nextStart < totalSections) {
                setStartSection(nextStart);
                setEndSection(nextEnd);
              }
            }}
            disabled={endSection >= totalSections}
            className={styles.navButton}
          >
            Next 20 →
          </button>

          <button
            onClick={() => {
              setStartSection(0);
              setEndSection(Math.min(50, totalSections));
            }}
            className={styles.presetButton}
          >
            First 50
          </button>
        </div>

        <button onClick={fetchProgressData} className={styles.refreshButton} disabled={loading}>
          {loading ? "Loading..." : "Refresh Data"}
        </button>

        {progressData && (
          <button onClick={exportToCSV} className={styles.exportButton}>
            Export CSV
          </button>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading && <div className={styles.loading}>Loading course progress...</div>}

      {progressData && (
        <>
          {/* Summary Cards */}
          <div className={styles.summary}>
            <div className={styles.summaryCard}>
              <h3>Showing Range</h3>
              <div className={styles.summaryNumber}>
                {startSection}-{Math.min(endSection - 1, totalSections - 1)}
              </div>
              <small>of {totalSections} total</small>
            </div>
            <div className={styles.summaryCard}>
              <h3>In This Range</h3>
              <div className={styles.summaryNumber}>{processedSections.length}</div>
            </div>
            <div className={`${styles.summaryCard} ${styles.complete}`}>
              <h3>Complete</h3>
              <div className={styles.summaryNumber}>
                {processedSections.filter((s) => s.overall_completion >= 90).length}
              </div>
            </div>
            <div className={`${styles.summaryCard} ${styles.partial}`}>
              <h3>Partial</h3>
              <div className={styles.summaryNumber}>
                {
                  processedSections.filter(
                    (s) => s.overall_completion >= 10 && s.overall_completion < 90
                  ).length
                }
              </div>
            </div>
            <div className={`${styles.summaryCard} ${styles.missing}`}>
              <h3>Missing</h3>
              <div className={styles.summaryNumber}>
                {processedSections.filter((s) => s.overall_completion < 10).length}
              </div>
            </div>
            <div className={styles.summaryCard}>
              <h3>Overall Progress</h3>
              <div className={styles.summaryNumber}>
                {processedSections.length > 0
                  ? Math.round(
                      (processedSections.reduce((sum, s) => sum + s.overall_completion, 0) /
                        processedSections.length) *
                        10
                    ) / 10
                  : 0}
                %
              </div>
            </div>
          </div>

          {/* Progress Table */}
          <div className={styles.tableContainer}>
            <table className={styles.progressTable}>
              <thead>
                <tr>
                  <th onClick={() => handleSort("section_id")} className={styles.sortable}>
                    Section ID {getSortIcon("section_id")}
                  </th>
                  <th onClick={() => handleSort("section_name")} className={styles.sortable}>
                    Section Name {getSortIcon("section_name")}
                  </th>
                  <th onClick={() => handleSort("overall_completion")} className={styles.sortable}>
                    Overall {getSortIcon("overall_completion")}
                  </th>
                  <th onClick={() => handleSort("vocabulary")} className={styles.sortable}>
                    Vocabulary {getSortIcon("vocabulary")}
                  </th>
                  <th>Passages</th>
                  <th>Fill In The Blank</th>
                  <th>Conversations</th>
                  <th>Item Guess</th>
                  <th>Reconstruct</th>
                  <th>Item Describe</th>
                  <th>Image List</th>
                  <th>Sentence Comp</th>
                </tr>
              </thead>
              <tbody>
                {displayedSections.map((section) => (
                  <tr key={section.section_id}>
                    <td className={styles.sectionId}>{section.section_id}</td>
                    <td className={styles.sectionName}>{section.section_name}</td>
                    <td className={styles.overallCompletion}>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${section.overall_completion}%` }}
                        ></div>
                        <span className={styles.progressText}>{section.overall_completion}%</span>
                      </div>
                    </td>
                    <td
                      className={getCellClassName(
                        section,
                        "vocabulary",
                        getStatusColor(section.vocabulary)
                      )}
                    >
                      {getStatusIcon(
                        section.vocabulary,
                        isComponentDisabled(section, "vocabulary"),
                        "vocabulary"
                      )}{" "}
                      {section.vocabulary.total}
                    </td>
                    <td
                      className={getCellClassName(
                        section,
                        "passages",
                        getStatusColor(section.passages)
                      )}
                    >
                      {getStatusIcon(section.passages, isComponentDisabled(section, "passages"))}{" "}
                      {section.passages.total}
                    </td>
                    <td
                      className={getCellClassName(
                        section,
                        "practice_sentences",
                        getStatusColor(section.practice_sentences)
                      )}
                    >
                      {getStatusIcon(
                        section.practice_sentences,
                        isComponentDisabled(section, "practice_sentences")
                      )}{" "}
                      {section.practice_sentences.total}
                    </td>
                    <td
                      className={getCellClassName(
                        section,
                        "conversation_objectives",
                        getStatusColor(section.conversation_objectives)
                      )}
                    >
                      {getStatusIcon(
                        section.conversation_objectives,
                        isComponentDisabled(section, "conversation_objectives")
                      )}{" "}
                      {section.conversation_objectives.total}
                    </td>
                    <td
                      className={getCellClassName(
                        section,
                        "item_guess",
                        getStatusColor(section.item_guess)
                      )}
                    >
                      {getStatusIcon(
                        section.item_guess,
                        isComponentDisabled(section, "item_guess")
                      )}{" "}
                      {section.item_guess.total}
                    </td>
                    <td
                      className={getCellClassName(
                        section,
                        "reconstruct_exercises",
                        getStatusColor(section.reconstruct_exercises)
                      )}
                    >
                      {getStatusIcon(
                        section.reconstruct_exercises,
                        isComponentDisabled(section, "reconstruct_exercises")
                      )}{" "}
                      {section.reconstruct_exercises.total}
                    </td>
                    <td
                      className={getCellClassName(
                        section,
                        "item_describe",
                        getStatusColor(section.item_describe)
                      )}
                    >
                      {getStatusIcon(
                        section.item_describe,
                        isComponentDisabled(section, "item_describe")
                      )}{" "}
                      {section.item_describe.total}
                    </td>
                    <td
                      className={getCellClassName(
                        section,
                        "image_list",
                        getStatusColor(section.image_list)
                      )}
                    >
                      {getStatusIcon(
                        section.image_list,
                        isComponentDisabled(section, "image_list")
                      )}{" "}
                      {section.image_list.total}
                    </td>
                    <td
                      className={getCellClassName(
                        section,
                        "sentence_comprehension",
                        getStatusColor(section.sentence_comprehension)
                      )}
                    >
                      {getStatusIcon(
                        section.sentence_comprehension,
                        isComponentDisabled(section, "sentence_comprehension")
                      )}{" "}
                      {section.sentence_comprehension.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {displayedSections.length === 0 && (
            <div className={styles.noResults}>No sections match the current filter criteria.</div>
          )}
        </>
      )}
    </div>
  );
};

export default CourseProgress;
